import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject, take, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { AlertService } from 'src/app/services/alert.service';
import { AlertType } from 'src/app/shared/types/alert.types';

@Component({
  selector: 'ness-manage-details-alert',
  templateUrl: './manage-details-alert.component.html',
  providers: [MessageService]
})
export class ManageDetailsAlertComponent implements OnInit {
  @Input() pageType: string = null; // CrimeGunCase; CrimeGunDetails
  @Input() crimeGunID: string = null; // CrimeGunCase; CrimeGunDetails
  @Input() checkboxLabel: string = "";
  @Output() showManageAlertForm = new EventEmitter<boolean>();
  @ViewChild('manageAlertBtn', { static: true }) manageAlertBtn: ElementRef;

  _alert: ServerModels.CrimeGunCaseAlertsViewModel = null;
  hasAlertSubscription: boolean = false;
  displayAlertNote: boolean = false;
  displayEditButton: boolean = false;
  displayErrorStatus: boolean = false;
  errorMessage: string = 'An error occurred.';
  tempAlertNote: string = "";
  alertNote: string = "";
  alertsUserRequestId: number = -1;
  public showMenu: boolean = false;

  alertFormGroup: FormGroup = this.fb.group({
    hasSubscription: false,
    alertNote: [{ value: '', disabled: true }, Validators.maxLength(150)]
  });

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private authService: AuthService,
    private fb: FormBuilder,
    public messageService: MessageService,
    private alertService: AlertService,
    public hostElement: ElementRef
  ) { }

  ngOnInit(): void {
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      if (this.pageType === "CrimeGunCase") {
        this.alertService.GetCrimeGunAlertsByUser().subscribe((a: ServerModels.CrimeGunCaseAlertsViewModel[]) => {
          if (a && a.length > 0) {
            this._alert = a[0];
            const _hasSubscription: boolean = a.length > 0;
            this.alertsUserRequestId = a[0].AlertsUserRequest_CrimeGunCasesID;
            this.populateAlertForm(_hasSubscription, a[0].AlertName);
          }
          else {
            this.hasAlertSubscription = false;
            this.alertFormGroup.get('hasSubscription').setValue(false);
            this.displayEditButton = false;
          }
        });
      }
    }

    if (this.pageType === "CrimeGunDetails") {
      this.alertService.hasAlertSubscription(<AlertType>'Crime Gun', this.crimeGunID).subscribe((b: ServerModels.CrimeGunAlertViewModel) => {
        if (b) {
          this.populateAlertForm(!b.IsDisabled, b.AlertNote);
        }
      });
    }
  }

  private populateAlertForm(hasSubscription: boolean, alertNote: string) {
    this.alertFormGroup.get('hasSubscription').setValue(hasSubscription);
    this.alertFormGroup.get('alertNote').setValue(alertNote);
    this.alertFormGroup.get('alertNote').disable();
    this.hasAlertSubscription = hasSubscription;
    this.alertNote = alertNote;
    this.tempAlertNote = alertNote;
    this.displayEditButton = true;
  }

  public onSubscribeChange(evt: Event) {
    this.messageService.clear();
    if (!!evt.target['checked']) {
      this.displayAlertNote = true;
    } else {
      this.displayEditButton = false;
      let _subscription = this.alertFormGroup.get('hasSubscription').value;
      let _alertNote = this.alertFormGroup.get('alertNote').value;
      if (this.pageType === "CrimeGunDetails") {
        let crimeGunAlert: ServerModels.CrimeGunAlertViewModel = {
          AlertType: 'Crime Gun',
          AlertTargetId: this.crimeGunID,
          AlertNote: _alertNote,
          IsDisabled: _subscription
        } as ServerModels.CrimeGunAlertViewModel;
        this.submitUnsubscribeAlert(crimeGunAlert);
      }

      if (this.pageType === "CrimeGunCase") {
        if (!!this._alert)
          this.onUnsubscribeCaseAlert(this._alert.AlertsUserRequest_CrimeGunCasesID);
      }
    }
  }

  private onUnsubscribeCaseAlert(id: number): void {

    this.alertService.DeleteCrimeGunCaseAlertByID(id).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (isDeleted:boolean) => {
        if (isDeleted) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'You have successfully unsubscribed from receiving email alerts when a Crime Gun ID linked to your agency has New Data in NESS.', sticky: true });
          this.hasAlertSubscription = false;
          this.alertsUserRequestId = -1;
          this._alert = null;
          this.tempAlertNote = "";
        }
      },
      complete: () => { },
      error: (err:any) => {
        this.errorMessage = `${err} Unable to unsubscribe from email alerts.`;
        this.displayErrorStatus = true;
      },
    });
  }

  private submitUnsubscribeAlert(crimeGunAlert: ServerModels.CrimeGunAlertViewModel) {
    this.alertService.unsubscribeAlert(crimeGunAlert).subscribe({
      next: (success: boolean) => {
        if (success) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'You have successfully unsubscribed from receiving email alerts when NIBIN or trace data associated with this crime gun is updated.', sticky: true });
          this.hasAlertSubscription = false;
        }
      },
      error: (err: any) => {
        this.errorMessage = `${err} Unable to unsubscribe from email alerts for this crime gun.`;
        this.displayErrorStatus = true;
      }
    });
  }

  public onCloseAlertNote(evt: Event) {

    this.alertFormGroup.get('alertNote').enable();
    this.alertFormGroup.get('alertNote').setValue(this.alertNote);
    this.alertFormGroup.get('alertNote').disable();
    this.tempAlertNote = this.alertNote;
    if (this.hasAlertSubscription) {
      this.displayEditButton = true;
      this.displayAlertNote = false;
    } else {
      this.onSubmitAlertNote(evt);
    }
  }

  public onSubmitAlertNote(evt: Event) {
    if (evt instanceof PointerEvent) {
      (<Event>evt).stopPropagation();
    }

    let _subscription = this.alertFormGroup.get('hasSubscription').value;
    let _alertNote = this.tempAlertNote;

    if (this.pageType === "CrimeGunDetails") {
      let crimeGunAlert: ServerModels.CrimeGunAlertViewModel = {
        AlertType: 'Crime Gun',
        AlertTargetId: this.crimeGunID,
        AlertNote: _alertNote,
        IsDisabled: !_subscription
      } as ServerModels.CrimeGunAlertViewModel;
      this.submitSubscribeAlert(crimeGunAlert);
    }

    if (this.pageType === "CrimeGunCase") {
      this.onSubscribeCaseAlert(_alertNote);
    }
  }

  private submitSubscribeAlert(CrimeGunAlert: ServerModels.CrimeGunAlertViewModel) {
    this.alertService.subscribeAlert(CrimeGunAlert).subscribe({
      next: (success: boolean) => {
        if (success) {
          this.alertUpdateCompleted(CrimeGunAlert.AlertNote, 'You have successfully subscribed to receive email alerts when NIBIN or trace data associated with this crime gun is updated.');
        }
      },
      error: (err: any) => {
        this.errorMessage = `${err} Unable to subscribe to email alerts for this crime gun.`;
        this.displayErrorStatus = true;
      }
    });
  }

  private onSubscribeCaseAlert(note: string) {
    let alert = {
      AlertsUserRequest_CrimeGunCasesID: this._alert ? this._alert.AlertsUserRequest_CrimeGunCasesID : null,
      ApplicationUserID: null,
      AlertName: note,
      FieldDivisionID: null,
      StateID: null,
      ENESS_REF_FieldDivision: null,
      ENESS_REF_State: null,
      ENESS_XREF_AlertCrimeGunCasesFieldDivisionGroups: null,
      ENESS_XREF_AlertCrimeGunCasesCounties: null,
      ENESS_XREF_AlertCrimeGunCasesAgencies: null
    } as ServerModels.ENESS_AlertsUserRequest_CrimeGunCases;
    this.alertService.AddOrUpdateCrimeGunCaseAlert(alert).pipe(take(1)).subscribe({
      next: (data: ServerModels.ENESS_AlertsUserRequest_CrimeGunCases) => {
      if(data) {
        this.alertUpdateCompleted(data.AlertName, 'You have successfully subscribed to receive email alerts when a Crime Gun ID linked to your agency has New Data in NESS.');
        this._alert = data;
      }
    },
      error: (err: any) => {
        this.errorMessage = `${err} Unable to subscribe to email alerts for this crime gun.`;
        this.displayErrorStatus = true;
      }
    });
  }

  private alertUpdateCompleted(alertName: string, message: string) {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: message, sticky: true, life: 36000 });
    this.hasAlertSubscription = true;
    this.alertNote = alertName;
    this.alertFormGroup.get('alertNote').enable();
    this.alertFormGroup.get('alertNote').setValue(this.alertNote);
    this.alertFormGroup.get('alertNote').disable();
    this.displayEditButton = true;
    this.displayAlertNote = false;
  }

  public onEditAlertNote() {
    this.displayAlertNote = true;
  }
}
